import { passes, type ColumnSelection } from './columnFilters'

/**
 * The pay period schedule report (docs/product/reports.md): every officer's
 * published schedule for one pay period, a day at a time, with the shift they
 * work, their days off, and their leave by type. A unit is a team. Pure, so
 * the screen and the exports read one result.
 */
export type GridPerson = {
  employeeId: string
  identifier: string
  employee: string
  teamId: string
  title: string
  shift: string
}

export type GridEntry = { employeeId: string; date: string; category: string; label: string }

// An entry as a team published it, with the team that published it.
export type PublishedEntry = GridEntry & { teamId: string }

export type GridPlacement = { employeeId: string; teamId: string; effectiveFrom: string; effectiveTo: string | null }

/**
 * One entry for each officer and date. When an officer transferred during the
 * period, the old team's snapshot and the new team's can both name the same
 * date; the entry kept is the one from the team the officer was placed on that
 * date, so a later snapshot does not overwrite the right schedule with a stale
 * one. A date no placement explains keeps the later snapshot's entry.
 */
export function resolveEntries(entries: PublishedEntry[], placements: GridPlacement[]): GridEntry[] {
  const placedOn = (entry: PublishedEntry) =>
    placements.some(
      (placement) =>
        placement.employeeId === entry.employeeId &&
        placement.teamId === entry.teamId &&
        placement.effectiveFrom <= entry.date &&
        (!placement.effectiveTo || placement.effectiveTo >= entry.date),
    )
  const kept = new Map<string, { entry: PublishedEntry; placed: boolean }>()
  for (const entry of entries) {
    const key = `${entry.employeeId}|${entry.date}`
    const placed = placedOn(entry)
    const current = kept.get(key)
    if (!current || placed || !current.placed) kept.set(key, { entry, placed })
  }
  return Array.from(kept.values()).map(({ entry }) => ({ employeeId: entry.employeeId, date: entry.date, category: entry.category, label: entry.label }))
}

export type GridDay = { category: string; label: string }

export type PayPeriodRow = GridPerson & {
  key: string
  unit: string
  supervisorId: string | null
  // One per date of the period, in order; null where nothing is scheduled.
  cells: (GridDay | null)[]
  workDays: number
  daysOff: number
  // Days of each leave type, by the type's name.
  leave: Record<string, number>
  // Days on detail or marked unavailable.
  otherDays: number
}

// The values each column is narrowed to; a unit is named by its team id, a name by
// the employee id.
export type PayPeriodFilters = {
  unit?: ColumnSelection
  shift?: ColumnSelection
  title?: ColumnSelection
  employee?: ColumnSelection
}

export const NO_SHIFT = 'No shift'
export const NO_TITLE = 'No title'

// A day worked is a work or a training day.
const WORKED = new Set(['work', 'training'])

export function buildPayPeriodGrid(
  people: GridPerson[],
  entries: GridEntry[],
  dates: string[],
  teams: Map<string, string>,
  supervisors: Map<string, string>,
): PayPeriodRow[] {
  const byEmployee = new Map<string, Map<string, GridDay>>()
  for (const entry of entries) {
    const days = byEmployee.get(entry.employeeId) ?? new Map<string, GridDay>()
    days.set(entry.date, { category: entry.category, label: entry.label })
    byEmployee.set(entry.employeeId, days)
  }

  return people
    .map((person): PayPeriodRow => {
      const days = byEmployee.get(person.employeeId)
      const cells = dates.map((date) => days?.get(date) ?? null)
      const leave: Record<string, number> = {}
      let workDays = 0
      let daysOff = 0
      let otherDays = 0
      for (const cell of cells) {
        if (!cell) continue
        if (WORKED.has(cell.category)) workDays += 1
        else if (cell.category === 'off') daysOff += 1
        else if (cell.category === 'leave') leave[cell.label] = (leave[cell.label] ?? 0) + 1
        else otherDays += 1
      }
      return {
        ...person,
        key: person.employeeId,
        title: person.title || NO_TITLE,
        shift: person.shift || NO_SHIFT,
        unit: teams.get(person.teamId) ?? 'Unknown unit',
        supervisorId: supervisors.get(person.teamId) ?? null,
        cells,
        workDays,
        daysOff,
        leave,
        otherDays,
      }
    })
    .sort((a, b) => a.unit.localeCompare(b.unit) || a.shift.localeCompare(b.shift) || a.title.localeCompare(b.title) || a.employee.localeCompare(b.employee) || a.identifier.localeCompare(b.identifier))
}

export function filterPayPeriodGrid(rows: PayPeriodRow[], filters: PayPeriodFilters): PayPeriodRow[] {
  return rows.filter(
    (row) =>
      passes(filters.unit, row.teamId) &&
      passes(filters.shift, row.shift) &&
      passes(filters.title, row.title) &&
      passes(filters.employee, row.employeeId),
  )
}

// The leave types that appear in the rows, so each gets a column.
export function leaveTypesIn(rows: PayPeriodRow[]): string[] {
  return Array.from(new Set(rows.flatMap((row) => Object.keys(row.leave)))).sort((a, b) => a.localeCompare(b))
}

export function leaveDays(row: PayPeriodRow): number {
  return Object.values(row.leave).reduce((sum, days) => sum + days, 0)
}

// What a day shows: nothing where nothing is scheduled, "Off" for a day off,
// and otherwise the schedule's own label (the hours, or the type of leave).
export function cellText(cell: GridDay | null): string {
  if (!cell) return ''
  return cell.category === 'off' ? 'Off' : cell.label
}
